import React, { useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@components/data-table';
import { TooltipTop } from '@components/tooltips';
import { FiCopy } from 'react-icons/fi';
import { AiOutlineDelete, AiOutlineEdit } from 'react-icons/ai';
import { AirflowXcomEntry } from '../../types';
import { formatDateTime } from '../../utils';
import styles from '../../styles.module.scss';

type XcomTableProps = {
    entries: AirflowXcomEntry[];
    isLoading: boolean;
    onCopyValue: (entry: AirflowXcomEntry) => void;
    onEdit: (entry: AirflowXcomEntry) => void;
    onDelete: (entry: AirflowXcomEntry) => void;
};

/*
 * XCom entries table for the DAG-run-task detail's XCom tab
 */
export function XcomTable({ entries, isLoading, onCopyValue, onEdit, onDelete }: XcomTableProps) {
    const columns = useMemo<ColumnDef<AirflowXcomEntry>[]>(
        () => [
            { id: 'key', header: 'Key', enableSorting: false, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.key}</span> },
            { id: 'dag', header: 'Dag', enableSorting: false, cell: ({ row }) => row.original.dag_display_name ?? row.original.dag_id },
            { id: 'run_id', header: 'Dag Run ID', enableSorting: false, cell: ({ row }) => <span style={{ fontFamily: 'monospace' }}>{row.original.run_id}</span> },
            { id: 'run_after', header: 'Run After', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.execution_date ?? row.original.run_after) },
            { id: 'task_id', header: 'Task', enableSorting: false, cell: ({ row }) => row.original.task_id },
            { id: 'map_index', header: 'Map Index', enableSorting: false, cell: ({ row }) => ((row.original.map_index ?? -1) >= 0 ? row.original.map_index : '—') },
            { id: 'timestamp', header: 'Timestamp', enableSorting: false, cell: ({ row }) => formatDateTime(row.original.timestamp) },
            {
                id: 'value',
                header: 'Value',
                enableSorting: false,
                cell: ({ row }) => {
                    // Confirmed from Airflow's own xcom_schema.py: the LIST endpoint's schema
                    // has no `value` field at all — it's only present when reading a single
                    // entry (values can be large). JSON.stringify(undefined) returns the
                    // literal value `undefined`, not a string, so calling .length on it here
                    // used to crash the whole app the moment any XCom entry actually loaded.
                    const value = row.original.value;
                    if (value === undefined) return <span style={{ color: '#666a8d' }}>—</span>;
                    const text = typeof value === 'string' ? value : JSON.stringify(value);
                    return <span style={{ fontFamily: 'monospace', color: '#666a8d' }}>{text.length > 60 ? `${text.slice(0, 60)}…` : text}</span>;
                },
            },
            {
                id: 'actions',
                header: 'Actions',
                enableSorting: false,
                cell: ({ row }) => (
                    <span style={{ display: 'flex', gap: 12 }}>
                        <TooltipTop title="Copy Value">
                            <span className={styles.actionIcon} onClick={() => onCopyValue(row.original)}>
                                <FiCopy />
                            </span>
                        </TooltipTop>
                        <TooltipTop title="Edit">
                            <span className={styles.actionIcon} onClick={() => onEdit(row.original)}>
                                <AiOutlineEdit />
                            </span>
                        </TooltipTop>
                        <TooltipTop title="Delete">
                            <span className={`${styles.actionIcon} ${styles.destructive}`} onClick={() => onDelete(row.original)}>
                                <AiOutlineDelete />
                            </span>
                        </TooltipTop>
                    </span>
                ),
            },
        ],
        [onCopyValue, onEdit, onDelete]
    );

    return <DataTable data={entries} columns={columns} isLoading={isLoading} rowKey={(entry) => entry.key} emptyText="No XCom entries" />;
}
